import { findFirstFrameNode, getAllSceneNodes } from "@/document/model/document";
import type { CurvaDocument } from "@/document/model/document";
import { frameCornerRadii, type FrameNode, type PrimitiveNode, type SceneNode, type TextNode, type VectorNetworkNode } from "@/document/model/nodes";
import { rasterAssetDataUrl } from "@/document/assets/rasterAssets";
import { oklchToHex, oklchToRgba } from "@/document/model/color";
import {
  gradientEndpointsForBounds,
  normalizeFillStack,
  normalizeImagePaint,
  normalizeGradientPaint,
  normalizeStrokeStack,
  radialGradientGeometryForBounds,
  sampleGradientPaint,
  type NodeEffect,
  type BackgroundBlurEffect,
  type Paint,
} from "@/document/model/styles";
import { imageRectForPaint } from "@/document/operations/imagePaintGeometry";
import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";
import { arcOvalToSvgPath, polygonToSvgPoints, primitiveToPolygon } from "@/document/operations/primitiveGeometry";
import { isClosedVectorNetwork } from "@/document/operations/vectorGeometry";
import { filledVectorRegionContourGroups, vectorRegionContourGroups, vectorRegionGeometries } from "@/document/operations/vectorRegions";
import { textNodeToSvgPaths } from "./textVectorize";
import { transformRotationOrigin } from "@/lib/math/rotation";

export interface SvgExportOptions {
  frameId?: string;
}

export async function exportDocumentToSvg(document: CurvaDocument, options: SvgExportOptions = {}): Promise<string> {
  const frame = options.frameId
    ? getAllSceneNodes(document).find((candidate): candidate is FrameNode => candidate.type === "frame" && candidate.id === options.frameId)
    : findFirstFrameNode(document);

  if (!frame) {
    throw new Error("No frame available for SVG export.");
  }

  return exportFrameToSvg(frame);
}

export async function exportFrameToSvg(frame: FrameNode): Promise<string> {
  const context: SvgExportContext = { defs: [] };
  const rawContent = await childrenToSvg(frame.children, context);
  const content = frame.clipsContent ? wrapFrameChildrenWithClip(frame, rawContent, context) : rawContent;
  const defs = context.defs.length > 0 ? `  <defs>\n    ${context.defs.join("\n    ")}\n  </defs>` : "";
  const { x, y, width, height } = frame.bounds;
  const framePath = roundedRectPathForBounds(frame.bounds, normalizedFrameCornerRadii(frame));
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${height}" viewBox="${x} ${y} ${width} ${height}" enable-background="new ${x} ${y} ${width} ${height}">`,
    defs,
    `  ${layeredElementSvg(frame, context, attrs => framePath
      ? `<path id="${frame.id}" d="${framePath}" ${attrs} />`
      : `<rect id="${frame.id}" x="${x}" y="${y}" width="${width}" height="${height}" ${attrs} />`)}`,
    content ? `  ${content}` : "",
    "</svg>",
  ]
    .filter(Boolean)
    .join("\n");
}

export async function exportSceneNodeToSvg(node: SceneNode): Promise<string> {
  if (node.type === "frame") {
    return exportFrameToSvg(node);
  }

  const context: SvgExportContext = { defs: [] };
  const content = await nodeToSvg(node, context);
  const defs = context.defs.length > 0 ? `  <defs>\n    ${context.defs.join("\n    ")}\n  </defs>` : "";
  const { x, y, width, height } = normalizeSvgBounds(node.bounds);
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${formatNumber(width)}" height="${formatNumber(height)}" viewBox="${formatNumber(x)} ${formatNumber(y)} ${formatNumber(width)} ${formatNumber(height)}" enable-background="new ${formatNumber(x)} ${formatNumber(y)} ${formatNumber(width)} ${formatNumber(height)}">`,
    defs,
    content ? `  ${content}` : "",
    "</svg>",
  ]
    .filter(Boolean)
    .join("\n");
}

interface SvgExportContext {
  defs: string[];
}

async function nodeToSvg(node: SceneNode, context: SvgExportContext): Promise<string> {
  if (!node.visible) {
    return "";
  }

  if (node.type === "primitive") {
    return primitiveToSvg(node, context);
  }

  if (node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    return `<g id="${node.id}"${nodeTransformAttribute(node)}>\n  ${await childrenToSvg(node.children, context)}\n</g>`;
  }

  if (node.type === "frame") {
    const { x, y, width, height } = node.bounds;
    const children = await childrenToSvg(node.children, context);
    const framePath = roundedRectPathForBounds(node.bounds, normalizedFrameCornerRadii(node));
    return [
      `<g id="${node.id}"${nodeTransformAttribute(node)}>`,
      `  ${layeredElementSvg(node, context, attrs => framePath
        ? `<path d="${framePath}" ${attrs} />`
        : `<rect x="${x}" y="${y}" width="${width}" height="${height}" ${attrs} />`)}`,
      node.clipsContent ? wrapFrameChildrenWithClip(node, children, context) : children,
      "</g>",
    ].filter(Boolean).join("\n");
  }

  if (node.type === "shape") {
    return layeredElementSvg(node, context, attrs => `<path id="${node.id}" d="${node.path}" ${attrs} fill-rule="evenodd" />`);
  }

  if (node.type === "boolean") {
    return layeredElementSvg(node, context, attrs => `<path id="${node.id}" d="${node.resolved.path}" ${attrs} fill-rule="evenodd" data-curva-boolean="${node.operation}" />`);
  }

  if (node.type === "vectorNetwork") {
    return vectorNetworkToSvg(node, context);
  }

  if (node.type === "text") {
    return textToSvg(node, context);
  }

  return "";
}

async function childrenToSvg(children: SceneNode[], context: SvgExportContext): Promise<string> {
  const output: string[] = [];
  let pending: SceneNode[] = [];
  let currentMask: SceneNode | undefined;

  async function flushPending(): Promise<void> {
    if (pending.length === 0) {
      return;
    }
    const content = (await Promise.all(pending.map(node => nodeToSvg(node, context)))).filter(Boolean).join("\n  ");
    pending = [];
    if (!content) {
      return;
    }
    if (!currentMask) {
      output.push(content);
      return;
    }
    const clipId = defineMaskClipPath(currentMask, context);
    output.push(`<g clip-path="url(#${clipId})" data-curva-mask-group="${escapeAttribute(currentMask.id)}">\n  ${content}\n</g>`);
  }

  for (const child of children) {
    if (child.isMask) {
      await flushPending();
      currentMask = child.visible ? child : undefined;
      continue;
    }
    pending.push(child);
  }

  await flushPending();
  return output.join("\n  ");
}

function defineMaskClipPath(mask: SceneNode, context: SvgExportContext): string {
  const clipId = `mask_${mask.id}`;
  if (!context.defs.some(def => def.includes(`id="${clipId}"`))) {
    const transform = nodeTransformAttribute(mask);
    const content = nodeClipPathContent(mask);
    context.defs.push(`<clipPath id="${clipId}" clipPathUnits="userSpaceOnUse" data-curva-mask-node="${escapeAttribute(mask.id)}">${transform ? `<g${transform}>${content}</g>` : content}</clipPath>`);
  }
  return clipId;
}

function wrapFrameChildrenWithClip(frame: FrameNode, content: string, context: SvgExportContext): string {
  if (!content) {
    return "";
  }

  const clipId = `clip_${frame.id}`;
  const { x, y, width, height } = frame.bounds;
  const clipPath = roundedRectPathForBounds(frame.bounds, normalizedFrameCornerRadii(frame));
  if (!context.defs.some(def => def.includes(`id="${clipId}"`))) {
    context.defs.push(
      clipPath
        ? `<clipPath id="${clipId}" clipPathUnits="userSpaceOnUse"><path d="${clipPath}" /></clipPath>`
        : `<clipPath id="${clipId}" clipPathUnits="userSpaceOnUse"><rect x="${x}" y="${y}" width="${width}" height="${height}" /></clipPath>`,
    );
  }

  return `<g clip-path="url(#${clipId})">\n  ${content}\n</g>`;
}

function primitiveToSvg(node: PrimitiveNode, context: SvgExportContext): string {
  const { x, y, width, height } = node.bounds;

  if (node.primitive.kind === "oval") {
    const { arcStart = 0, arcSweep = 360, ratio = 0 } = node.primitive;
    const isFullCircle = arcSweep >= 360 && ratio === 0;
    if (isFullCircle) {
      return layeredElementSvg(node, context, attrs => `<ellipse id="${node.id}" cx="${x + width / 2}" cy="${y + height / 2}" rx="${width / 2}" ry="${height / 2}" ${attrs} />`);
    }
    const d = arcOvalToSvgPath(node.bounds, arcStart, arcSweep, ratio);
    return layeredElementSvg(node, context, attrs => `<path id="${node.id}" d="${d}" fill-rule="evenodd" ${attrs} />`);
  }

  if (node.primitive.kind === "line") {
    return layeredElementSvg(node, context, attrs => `<line id="${node.id}" x1="${x}" y1="${y}" x2="${x + width}" y2="${y + height}" ${attrs} />`, { fills: false });
  }

  if (node.primitive.kind === "polygon" || node.primitive.kind === "star") {
    return layeredElementSvg(node, context, attrs => `<polygon id="${node.id}" points="${polygonToSvgPoints(primitiveToPolygon(node))}" ${attrs} />`);
  }

  if (node.primitive.kind === "rectangle") {
    return rectangleToSvg(node, context);
  }

  const radius = 0;
  return layeredElementSvg(node, context, attrs => `<rect id="${node.id}" x="${x}" y="${y}" width="${width}" height="${height}" rx="${radius}" ${attrs} />`);
}

function rectangleToSvg(node: PrimitiveNode, context: SvgExportContext): string {
  const { x, y, width, height } = node.bounds;
  const [topLeft, topRight, bottomRight, bottomLeft] = normalizedCornerRadii(node);
  if (topLeft === 0 && topRight === 0 && bottomRight === 0 && bottomLeft === 0) {
    return layeredElementSvg(node, context, attrs => `<rect id="${node.id}" x="${x}" y="${y}" width="${width}" height="${height}" ${attrs} />`);
  }

  const path = roundedRectanglePath(node);
  return layeredElementSvg(node, context, attrs => `<path id="${node.id}" d="${path}" ${attrs} />`);
}

function roundedRectanglePath(node: PrimitiveNode): string {
  return roundedRectPathForBounds(node.bounds, normalizedCornerRadii(node)) ?? "";
}

function roundedRectPathForBounds(
  bounds: { x: number; y: number; width: number; height: number },
  radii: [number, number, number, number],
): string | undefined {
  const { x, y, width, height } = bounds;
  const [topLeft, topRight, bottomRight, bottomLeft] = radii;
  if (topLeft === 0 && topRight === 0 && bottomRight === 0 && bottomLeft === 0) {
    return undefined;
  }
  const right = x + width;
  const bottom = y + height;
  const kappa = 0.5522847498307936;
  return [
    `M ${formatNumber(x + topLeft)} ${formatNumber(y)}`,
    `L ${formatNumber(right - topRight)} ${formatNumber(y)}`,
    topRight > 0
      ? `C ${formatNumber(right - topRight + topRight * kappa)} ${formatNumber(y)} ${formatNumber(right)} ${formatNumber(y + topRight - topRight * kappa)} ${formatNumber(right)} ${formatNumber(y + topRight)}`
      : "",
    `L ${formatNumber(right)} ${formatNumber(bottom - bottomRight)}`,
    bottomRight > 0
      ? `C ${formatNumber(right)} ${formatNumber(bottom - bottomRight + bottomRight * kappa)} ${formatNumber(right - bottomRight + bottomRight * kappa)} ${formatNumber(bottom)} ${formatNumber(right - bottomRight)} ${formatNumber(bottom)}`
      : "",
    `L ${formatNumber(x + bottomLeft)} ${formatNumber(bottom)}`,
    bottomLeft > 0
      ? `C ${formatNumber(x + bottomLeft - bottomLeft * kappa)} ${formatNumber(bottom)} ${formatNumber(x)} ${formatNumber(bottom - bottomLeft + bottomLeft * kappa)} ${formatNumber(x)} ${formatNumber(bottom - bottomLeft)}`
      : "",
    `L ${formatNumber(x)} ${formatNumber(y + topLeft)}`,
    topLeft > 0
      ? `C ${formatNumber(x)} ${formatNumber(y + topLeft - topLeft * kappa)} ${formatNumber(x + topLeft - topLeft * kappa)} ${formatNumber(y)} ${formatNumber(x + topLeft)} ${formatNumber(y)}`
      : "",
    "Z",
  ].filter(Boolean).join(" ");
}

function normalizedCornerRadii(node: PrimitiveNode): [number, number, number, number] {
  if (node.primitive.kind !== "rectangle") {
    return [0, 0, 0, 0];
  }

  const maxRadius = Math.min(Math.abs(node.bounds.width), Math.abs(node.bounds.height)) / 2;
  return node.primitive.cornerRadii.map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number];
}

function normalizedFrameCornerRadii(frame: FrameNode): [number, number, number, number] {
  const maxRadius = Math.min(Math.abs(frame.bounds.width), Math.abs(frame.bounds.height)) / 2;
  return frameCornerRadii(frame).map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number];
}

function vectorNetworkToSvg(node: VectorNetworkNode, context: SvgExportContext): string {
  const path = vectorNetworkPathData(node.network);
  if (!path) {
    return "";
  }

  if (node.network.regions.length > 0) {
    const fillPaths = filledVectorRegionContourGroups(node.network)
      .flatMap((contours, regionIndex) => {
        const regionPath = contours.map(contourPathData).filter(Boolean).join(" ");
        if (!regionPath) {
          return [];
        }

        return normalizeFillStack(node.style).map((fill, fillIndex) => (
          `<path id="${node.id}_region_${regionIndex}_fill_${fillIndex}" d="${regionPath}" ${styleAttributes(node, context, {
            fillPaint: fill,
            fillIdSuffix: `region_${regionIndex}_stack_${fillIndex}`,
            stroke: "none",
          })} fill-rule="evenodd" />`
        ));
      });
    const strokePath = layeredElementSvg(node, context, attrs => `<path id="${node.id}" d="${path}" ${attrs} />`, { fills: false, wrapBackgroundBlur: false });
    return wrapWithBackgroundBlur(node, context, [...fillPaths, strokePath].filter(Boolean).join("\n"));
  }

  const fillGroups = vectorRegionContourGroups(node.network);
  if (fillGroups.some(group => group.length > 1)) {
    const fillPaths = fillGroups.flatMap((contours, groupIndex) => {
      const groupPath = contours.map(contourPathData).filter(Boolean).join(" ");
      if (!groupPath) {
        return [];
      }

      return normalizeFillStack(node.style).map((fill, fillIndex) => (
        `<path id="${node.id}_fill_group_${groupIndex}_${fillIndex}" d="${groupPath}" ${styleAttributes(node, context, {
          fillPaint: fill,
          fillIdSuffix: `fill_group_${groupIndex}_stack_${fillIndex}`,
          stroke: "none",
        })} fill-rule="evenodd" />`
      ));
    });
    const strokePath = layeredElementSvg(node, context, attrs => `<path id="${node.id}" d="${path}" ${attrs} />`, { fills: false, wrapBackgroundBlur: false });
    return wrapWithBackgroundBlur(node, context, [...fillPaths, strokePath].filter(Boolean).join("\n"));
  }

  return wrapWithBackgroundBlur(
    node,
    context,
    layeredElementSvg(node, context, attrs => `<path id="${node.id}" d="${path}" ${attrs} />`, {
      fills: isClosedVectorNetwork(node.network),
      wrapBackgroundBlur: false,
    }),
  );
}

async function textToSvg(node: TextNode, context: SvgExportContext): Promise<string> {
  const paths = await textNodeToSvgPaths(node);
  if (paths.length === 0) {
    return "";
  }

  if (paths.length === 1) {
    return wrapWithBackgroundBlur(node, context, `<path id="${node.id}" d="${paths[0]!.d}" ${styleAttributes(node, context, { fillPaint: paths[0]!.fill, stroke: "none" })} fill-rule="evenodd" data-curva-text-vectorized="true" />`);
  }

  const children = paths.map((path, index) => {
    return `<path d="${path.d}" ${styleAttributes(node, context, { fillPaint: path.fill, fillIdSuffix: `run_${index}`, stroke: "none" })} fill-rule="evenodd" data-curva-text-vectorized="true" />`;
  }).join("\n  ");
  return wrapWithBackgroundBlur(node, context, `<g id="${node.id}" data-curva-text-vectorized="true">\n  ${children}\n</g>`);
}

function vectorNetworkPathData(network: VectorNetwork): string {
  return vectorNetworkPathDataForEdges(network, network.edges.map(edge => edge.id));
}

function vectorNetworkPathDataForEdges(network: VectorNetwork, edgeIds: string[]): string {
  const commands: string[] = [];
  let currentVertexId: string | undefined;
  let subpathStartVertexId: string | undefined;
  const include = new Set(edgeIds);

  for (const edgeId of edgeIds) {
    const edge = network.edges.find(candidate => candidate.id === edgeId);
    if (!edge || !include.has(edge.id)) {
      continue;
    }
    const from = findVertex(network.vertices, edge.from.vertexId);
    const to = findVertex(network.vertices, edge.to.vertexId);
    if (!from || !to) {
      continue;
    }

    if (currentVertexId !== from.id) {
      commands.push(`M ${formatNumber(from.x)} ${formatNumber(from.y)}`);
      subpathStartVertexId = from.id;
    }

    commands.push(edgeToPathCommand(edge, from, to));
    currentVertexId = to.id;
    if (subpathStartVertexId && to.id === subpathStartVertexId) {
      commands.push("Z");
      currentVertexId = undefined;
      subpathStartVertexId = undefined;
    }
  }

  return commands.join(" ");
}

function contourPathData(contour: Array<{ x: number; y: number }>): string {
  const first = contour[0];
  if (!first) {
    return "";
  }

  return [
    `M ${formatNumber(first.x)} ${formatNumber(first.y)}`,
    ...contour.slice(1).map(point => `L ${formatNumber(point.x)} ${formatNumber(point.y)}`),
    "Z",
  ].join(" ");
}

function edgeToPathCommand(edge: VectorEdge, from: VectorVertex, to: VectorVertex): string {
  if (edge.from.handle && edge.to.handle) {
    return [
      "C",
      formatNumber(edge.from.handle.x),
      formatNumber(edge.from.handle.y),
      formatNumber(edge.to.handle.x),
      formatNumber(edge.to.handle.y),
      formatNumber(to.x),
      formatNumber(to.y),
    ].join(" ");
  }

  if (edge.from.handle) {
    return [
      "C",
      formatNumber(edge.from.handle.x),
      formatNumber(edge.from.handle.y),
      formatNumber(to.x),
      formatNumber(to.y),
      formatNumber(to.x),
      formatNumber(to.y),
    ].join(" ");
  }

  if (edge.to.handle) {
    return [
      "C",
      formatNumber(from.x),
      formatNumber(from.y),
      formatNumber(edge.to.handle.x),
      formatNumber(edge.to.handle.y),
      formatNumber(to.x),
      formatNumber(to.y),
    ].join(" ");
  }

  return `L ${formatNumber(to.x)} ${formatNumber(to.y)}`;
}

function findVertex(vertices: VectorVertex[], vertexId: string): VectorVertex | undefined {
  return vertices.find(vertex => vertex.id === vertexId);
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

function normalizeSvgBounds(bounds: { x: number; y: number; width: number; height: number }): { x: number; y: number; width: number; height: number } {
  const x1 = Math.min(bounds.x, bounds.x + bounds.width);
  const y1 = Math.min(bounds.y, bounds.y + bounds.height);
  const x2 = Math.max(bounds.x, bounds.x + bounds.width);
  const y2 = Math.max(bounds.y, bounds.y + bounds.height);
  return {
    x: x1,
    y: y1,
    width: Math.max(1, x2 - x1),
    height: Math.max(1, y2 - y1),
  };
}

function styleAttributes(
  node: SceneNode,
  context: SvgExportContext,
  overrides: { fill?: string; fillPaint?: Paint; fillIdSuffix?: string; stroke?: string; strokePaint?: Paint; strokeWidth?: number } = {},
): string {
  const fill = overrides.fill ?? paintToSvgPaint(overrides.fillPaint ?? normalizeFillStack(node.style).at(-1), node, overrides.fillIdSuffix ? `fill_${overrides.fillIdSuffix}` : "fill", context);
  const strokeStack = normalizeStrokeStack(node.style);
  const stroke = overrides.stroke ?? paintToSvgPaint(overrides.strokePaint ?? strokeStack[0]?.paint, node, "stroke", context);
  const strokeWidth = overrides.stroke === "none" ? 0 : overrides.strokeWidth ?? strokeStack[0]?.width ?? 0;
  const filter = effectsToSvgFilter(node, context);
  const effectMetadata = node.style.effects?.length ? ` data-curva-effects="${escapeAttribute(JSON.stringify(node.style.effects))}"` : "";
  const maskMetadata = node.isMask ? ` data-curva-mask="true"` : "";
  return `fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" opacity="${node.style.opacity}"${filter}${effectMetadata}${maskMetadata}`;
}

function nodeTransformAttribute(node: SceneNode): string {
  const rotation = node.transform.rotation;
  if (!Number.isFinite(rotation) || rotation === 0) {
    return "";
  }
  const origin = transformRotationOrigin(node.bounds, node.transform);
  return ` transform="rotate(${formatNumber(rotation)} ${formatNumber(origin.x)} ${formatNumber(origin.y)})"`;
}

function layeredElementSvg(
  node: SceneNode,
  context: SvgExportContext,
  element: (attributes: string) => string,
  options: { fills?: boolean; wrapBackgroundBlur?: boolean } = {},
): string {
  const includeFills = options.fills ?? true;
  const wrapBackgroundBlur = options.wrapBackgroundBlur ?? true;
  const fills = includeFills ? normalizeFillStack(node.style) : [];
  const strokes = normalizeStrokeStack(node.style);
  const layers = [
    ...fills.map((fill, index) => element(styleAttributes(node, context, {
      fillPaint: fill,
      fillIdSuffix: `stack_${index}`,
      stroke: "none",
    }))),
    ...strokes.map((stroke, index) => element(styleAttributes(node, context, {
      fill: "none",
      strokePaint: stroke.paint,
      stroke: paintToSvgPaint(stroke.paint, node, `stroke_stack_${index}`, context),
      strokeWidth: stroke.width,
    }))),
  ];

  if (layers.length === 0) {
    const content = element(styleAttributes(node, context, { fill: "none", stroke: "none" }));
    return wrapBackgroundBlur ? wrapWithBackgroundBlur(node, context, content) : content;
  }

  const content = layers.length === 1 ? layers[0]! : `<g id="${node.id}_paintStack">\n  ${layers.join("\n  ")}\n</g>`;
  return wrapBackgroundBlur ? wrapWithBackgroundBlur(node, context, content) : content;
}

function visibleBackgroundBlur(node: SceneNode): BackgroundBlurEffect | undefined {
  return node.style.effects?.find(
    (effect): effect is BackgroundBlurEffect =>
      effect.visible && effect.type === "backgroundBlur" && effect.blur > 0,
  );
}

function filterableEffects(node: SceneNode): NodeEffect[] {
  return node.style.effects?.filter(effect => effect.visible && effect.type !== "backgroundBlur") ?? [];
}

function wrapWithBackgroundBlur(node: SceneNode, context: SvgExportContext, content: string): string {
  const backgroundBlur = visibleBackgroundBlur(node);
  if (!backgroundBlur || !content) {
    return content;
  }

  const bounds = normalizeSvgBounds(node.bounds);
  const padding = Math.max(0, backgroundBlur.blur);
  const foreignX = bounds.x - padding;
  const foreignY = bounds.y - padding;
  const foreignWidth = bounds.width + padding * 2;
  const foreignHeight = bounds.height + padding * 2;
  const clipId = `bgblur_${node.id}_clip_path`;

  if (!context.defs.some(def => def.includes(`id="${clipId}"`))) {
    context.defs.push(
      `<clipPath id="${clipId}" clipPathUnits="userSpaceOnUse" transform="translate(${formatNumber(-foreignX)} ${formatNumber(-foreignY)})">${nodeClipPathContent(node)}</clipPath>`,
    );
  }

  const foreignObject = [
    `<foreignObject x="${formatNumber(foreignX)}" y="${formatNumber(foreignY)}" width="${formatNumber(foreignWidth)}" height="${formatNumber(foreignHeight)}" data-curva-bg-blur-radius="${formatNumber(backgroundBlur.blur)}">`,
    `<div xmlns="http://www.w3.org/1999/xhtml" style="backdrop-filter:blur(${formatNumber(backgroundBlur.blur)}px);clip-path:url(#${clipId});height:100%;width:100%"></div>`,
    `</foreignObject>`,
  ].join("");

  return `${foreignObject}\n  ${content}`;
}

function nodeClipPathContent(node: SceneNode): string {
  const { x, y, width, height } = normalizeSvgBounds(node.bounds);

  if (node.type === "primitive") {
    if (node.primitive.kind === "oval") {
      const { arcStart = 0, arcSweep = 360, ratio = 0 } = node.primitive;
      return arcSweep >= 360 && ratio === 0
        ? `<ellipse cx="${formatNumber(x + width / 2)}" cy="${formatNumber(y + height / 2)}" rx="${formatNumber(width / 2)}" ry="${formatNumber(height / 2)}" />`
        : `<path d="${arcOvalToSvgPath(node.bounds, arcStart, arcSweep, ratio)}" fill-rule="evenodd" />`;
    }

    if (node.primitive.kind === "rectangle") {
      const [topLeft, topRight, bottomRight, bottomLeft] = normalizedCornerRadii(node);
      if (topLeft === 0 && topRight === 0 && bottomRight === 0 && bottomLeft === 0) {
        return `<rect x="${formatNumber(x)}" y="${formatNumber(y)}" width="${formatNumber(width)}" height="${formatNumber(height)}" />`;
      }

      return `<path d="${roundedRectanglePath(node)}" />`;
    }

    const points = polygonToSvgPoints(primitiveToPolygon(node));
    return points ? `<polygon points="${points}" />` : "";
  }

  if (node.type === "group" || node.type === "frame" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    const children = node.type === "component" ? node.children : node.children;
    return children.map(child => `<g${nodeTransformAttribute(child)}>${nodeClipPathContent(child)}</g>`).join("");
  }

  if (node.type === "shape") {
    return `<path d="${node.path}" />`;
  }

  if (node.type === "vectorNetwork") {
    const path = vectorNetworkClipPathData(node.network);
    return path ? `<path d="${path}" fill-rule="evenodd" clip-rule="evenodd" />` : `<rect x="${formatNumber(x)}" y="${formatNumber(y)}" width="${formatNumber(width)}" height="${formatNumber(height)}" />`;
  }

  if (node.type === "boolean") {
    return `<path d="${node.resolved.path}" />`;
  }

  return `<rect x="${formatNumber(x)}" y="${formatNumber(y)}" width="${formatNumber(width)}" height="${formatNumber(height)}" />`;
}

function vectorNetworkClipPathData(network: VectorNetwork): string {
  const regionPath = vectorRegionGeometries(network)
    .filter(geometry => network.regions.length === 0 || geometry.region.filled !== false)
    .map(geometry => [exactVectorRegionPathData(network, geometry.edgeRefs), ...geometry.holes.map(contourPathData)].filter(Boolean).join(" "))
    .filter(Boolean)
    .join(" ");
  return regionPath || vectorNetworkPathData(network);
}

function exactVectorRegionPathData(network: VectorNetwork, edgeRefs: Array<{ edgeId: string; forward: boolean }>): string {
  const firstRef = edgeRefs[0];
  if (!firstRef) {
    return "";
  }
  const firstEdge = network.edges.find(edge => edge.id === firstRef.edgeId);
  const firstVertex = firstEdge ? endpointVertex(network, firstEdge, firstRef.forward, "from") : undefined;
  if (!firstEdge || !firstVertex) {
    return "";
  }

  const commands = [`M ${formatNumber(firstVertex.x)} ${formatNumber(firstVertex.y)}`];
  let startVertexId = firstVertex.id;
  let currentVertexId = firstVertex.id;
  for (const ref of edgeRefs) {
    const edge = network.edges.find(candidate => candidate.id === ref.edgeId);
    if (!edge) {
      continue;
    }
    const from = endpointVertex(network, edge, ref.forward, "from");
    const to = endpointVertex(network, edge, ref.forward, "to");
    if (!from || !to) {
      continue;
    }
    if (currentVertexId !== from.id) {
      commands.push(`M ${formatNumber(from.x)} ${formatNumber(from.y)}`);
      startVertexId = from.id;
    }
    commands.push(orientedEdgeToPathCommand(edge, from, to, ref.forward));
    currentVertexId = to.id;
  }
  if (currentVertexId === startVertexId) {
    commands.push("Z");
  }
  return commands.join(" ");
}

function endpointVertex(network: VectorNetwork, edge: VectorEdge, forward: boolean, endpoint: "from" | "to"): VectorVertex | undefined {
  const vertexId = endpoint === "from"
    ? (forward ? edge.from.vertexId : edge.to.vertexId)
    : (forward ? edge.to.vertexId : edge.from.vertexId);
  return findVertex(network.vertices, vertexId);
}

function orientedEdgeToPathCommand(edge: VectorEdge, from: VectorVertex, to: VectorVertex, forward: boolean): string {
  if (forward) {
    return edgeToPathCommand(edge, from, to);
  }
  const firstHandle = edge.to.handle;
  const secondHandle = edge.from.handle;
  if (firstHandle && secondHandle) {
    return [
      "C",
      formatNumber(firstHandle.x),
      formatNumber(firstHandle.y),
      formatNumber(secondHandle.x),
      formatNumber(secondHandle.y),
      formatNumber(to.x),
      formatNumber(to.y),
    ].join(" ");
  }
  if (firstHandle) {
    return [
      "C",
      formatNumber(firstHandle.x),
      formatNumber(firstHandle.y),
      formatNumber(to.x),
      formatNumber(to.y),
      formatNumber(to.x),
      formatNumber(to.y),
    ].join(" ");
  }
  if (secondHandle) {
    return [
      "C",
      formatNumber(from.x),
      formatNumber(from.y),
      formatNumber(secondHandle.x),
      formatNumber(secondHandle.y),
      formatNumber(to.x),
      formatNumber(to.y),
    ].join(" ");
  }
  return `L ${formatNumber(to.x)} ${formatNumber(to.y)}`;
}

function paintToSvgPaint(paint: Paint | undefined, node: SceneNode, target: string, context: SvgExportContext): string {
  if (!paint) {
    return "none";
  }

  if (paint.type === "solid") {
    return oklchToHex(paint.color);
  }

  const id = `${target}_${node.id}`;
  if (!context.defs.some(def => def.includes(`id="${id}"`))) {
    context.defs.push(
      paint.type === "image"
        ? imagePatternDef(id, node, paint)
        : paint.type === "linearGradient"
          ? linearGradientDef(id, node, paint)
          : radialGradientDef(id, node, paint),
    );
  }
  return `url(#${id})`;
}

function imagePatternDef(id: string, node: SceneNode, paint: Extract<Paint, { type: "image" }>): string {
  const normalized = normalizeImagePaint(paint);
  const source = rasterAssetDataUrl(normalized.assetId, normalized.source);
  if (!source) {
    return `<pattern id="${id}" patternContentUnits="objectBoundingBox" width="1" height="1" />`;
  }
  const imageRect = imageRectForPaint(node.bounds, normalized);
  const imageId = `image_${id}`;
  const flipX = (normalized.flipX ?? false) || node.transform.scaleX < 0;
  const flipY = (normalized.flipY ?? false) || node.transform.scaleY < 0;
  const transform = imagePatternTransform(node, imageRect, normalized.intrinsicWidth, normalized.intrinsicHeight, flipX, flipY);
  const imageMetadata = escapeAttribute(JSON.stringify({
    fillMode: normalized.fillMode,
    crop: normalized.crop,
    flipX: normalized.flipX,
    flipY: normalized.flipY,
    opacity: normalized.opacity,
    intrinsicWidth: normalized.intrinsicWidth,
    intrinsicHeight: normalized.intrinsicHeight,
    name: normalized.name,
    mimeType: normalized.mimeType,
    assetId: normalized.assetId,
  }));
  return [
    `<pattern id="${id}" patternContentUnits="objectBoundingBox" width="1" height="1">`,
    `  <use xlink:href="#${imageId}" transform="matrix(${formatNumber(transform.scaleX)} 0 0 ${formatNumber(transform.scaleY)} ${formatNumber(transform.translateX)} ${formatNumber(transform.translateY)})" opacity="${formatNumber(normalized.opacity)}" />`,
    `</pattern>`,
    `<image id="${imageId}" width="${formatNumber(normalized.intrinsicWidth)}" height="${formatNumber(normalized.intrinsicHeight)}" preserveAspectRatio="none" xlink:href="${escapeAttribute(source)}" data-curva-image-paint="${imageMetadata}" data-curva-image-adjustments="${escapeAttribute(JSON.stringify(normalized.adjustments))}" />`,
  ].join("\n    ");
}

function imagePatternTransform(
  node: SceneNode,
  imageRect: { x: number; y: number; width: number; height: number },
  intrinsicWidth: number,
  intrinsicHeight: number,
  flipX = false,
  flipY = false,
): { scaleX: number; scaleY: number; translateX: number; translateY: number } {
  const bounds = node.bounds;
  const width = Math.max(0.0001, bounds.width);
  const height = Math.max(0.0001, bounds.height);
  let scaleX = (imageRect.width / width) / Math.max(1, intrinsicWidth);
  let scaleY = (imageRect.height / height) / Math.max(1, intrinsicHeight);
  let translateX = (imageRect.x - bounds.x) / width;
  let translateY = (imageRect.y - bounds.y) / height;
  if (flipX) {
    scaleX = -scaleX;
    translateX += imageRect.width / width;
  }
  if (flipY) {
    scaleY = -scaleY;
    translateY += imageRect.height / height;
  }
  return { scaleX, scaleY, translateX, translateY };
}

function linearGradientDef(id: string, node: SceneNode, paint: Extract<Paint, { type: "linearGradient" }>): string {
  const normalized = normalizeGradientPaint(paint);
  const vector = gradientEndpointsForBounds(node.bounds, normalized);
  const stops = sampleGradientPaint(normalized, normalized.colorSpace === "oklch" ? 32 : normalized.stops.length);
  return [
    `<linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="${formatNumber(vector.start.x)}" y1="${formatNumber(vector.start.y)}" x2="${formatNumber(vector.end.x)}" y2="${formatNumber(vector.end.y)}" color-interpolation="sRGB">`,
    ...stops.map(stop => `  <stop offset="${formatNumber(stop.position * 100)}%" stop-color="${oklchToHex(stop.color)}" stop-opacity="${formatNumber(stop.color.alpha)}" />`),
    `</linearGradient>`,
  ].join("\n    ");
}

function radialGradientDef(id: string, node: SceneNode, paint: Extract<Paint, { type: "radialGradient" }>): string {
  const normalized = normalizeGradientPaint(paint);
  const geometry = radialGradientGeometryForBounds(node.bounds, normalized);
  const stops = sampleGradientPaint(normalized, normalized.colorSpace === "oklch" ? 32 : normalized.stops.length);
  return [
    `<radialGradient id="${id}" gradientUnits="userSpaceOnUse" cx="${formatNumber(geometry.center.x)}" cy="${formatNumber(geometry.center.y)}" r="${formatNumber(geometry.radius)}" color-interpolation="sRGB">`,
    ...stops.map(stop => `  <stop offset="${formatNumber(stop.position * 100)}%" stop-color="${oklchToHex(stop.color)}" stop-opacity="${formatNumber(stop.color.alpha)}" />`),
    `</radialGradient>`,
  ].join("\n    ");
}

function effectsToSvgFilter(node: SceneNode, context: SvgExportContext): string {
  const effects = filterableEffects(node);
  if (effects.length === 0) {
    return "";
  }

  const id = `filter_${node.id}`;
  if (!context.defs.some(def => def.includes(`id="${id}"`))) {
    context.defs.push(svgFilterDef(id, node, effects));
  }
  return ` filter="url(#${id})"`;
}

function svgFilterDef(id: string, node: SceneNode, effects: NodeEffect[]): string {
  const bounds = filterBounds(node, effects);
  const primitives: string[] = [];
  let current = "SourceGraphic";
  const overlays: string[] = [];

  effects.forEach((effect, index) => {
    if (effect.type === "dropShadow") {
      const result = `dropShadow_${index}`;
      primitives.push(...dropShadowPrimitives(effect, result));
      overlays.push(result);
      return;
    }

    if (effect.type === "innerShadow") {
      const result = `innerShadow_${index}`;
      primitives.push(...innerShadowPrimitives(effect, result));
      overlays.push(result);
      return;
    }

    if (effect.type === "layerBlur") {
      const blur = effect.mode === "progressive" ? Math.max(effect.start, effect.end) : effect.blur;
      const result = `layerBlur_${index}`;
      primitives.push(`<feGaussianBlur in="${current}" stdDeviation="${formatNumber(Math.max(0, blur))}" result="${result}" />`);
      current = result;
    }
  });

  let composite = current;
  overlays.forEach((overlay, index) => {
    const result = `effectComposite_${index}`;
    primitives.push(`<feComposite in="${overlay}" in2="${composite}" operator="over" result="${result}" />`);
    composite = result;
  });

  primitives.push(identityPrimitive(composite));

  return [
    `<filter id="${id}" filterUnits="userSpaceOnUse" x="${formatNumber(bounds.x)}" y="${formatNumber(bounds.y)}" width="${formatNumber(bounds.width)}" height="${formatNumber(bounds.height)}" color-interpolation-filters="sRGB">`,
    ...primitives.map(primitive => `  ${primitive}`),
    `</filter>`,
  ].join("\n    ");
}

function dropShadowPrimitives(effect: Extract<NodeEffect, { type: "dropShadow" }>, result: string): string[] {
  const color = effectColor(effect);
  return [
    `<feDropShadow dx="${formatNumber(effect.offsetX)}" dy="${formatNumber(effect.offsetY)}" stdDeviation="${formatNumber(Math.max(0, effect.blur))}" flood-color="${color.hex}" flood-opacity="${formatNumber(color.alpha)}" result="${result}" />`,
  ];
}

function innerShadowPrimitives(effect: Extract<NodeEffect, { type: "innerShadow" }>, result: string): string[] {
  const color = effectColor(effect);
  const offset = `${result}_offset`;
  const blur = `${result}_blur`;
  const mask = `${result}_mask`;
  const flood = `${result}_color`;
  return [
    `<feOffset in="SourceAlpha" dx="${formatNumber(effect.offsetX)}" dy="${formatNumber(effect.offsetY)}" result="${offset}" />`,
    `<feGaussianBlur in="${offset}" stdDeviation="${formatNumber(Math.max(0, effect.blur))}" result="${blur}" />`,
    `<feComposite in="${blur}" in2="SourceAlpha" operator="in" result="${mask}" />`,
    `<feFlood flood-color="${color.hex}" flood-opacity="${formatNumber(color.alpha)}" result="${flood}" />`,
    `<feComposite in="${flood}" in2="${mask}" operator="in" result="${result}" />`,
  ];
}

function filterBounds(node: SceneNode, effects: NodeEffect[]): { x: number; y: number; width: number; height: number } {
  const nodeBounds = normalizeSvgBounds(node.bounds);
  return effects.reduce((bounds, effect) => {
    if (effect.type === "dropShadow") {
      const padding = Math.max(0, effect.blur) * 3 + Math.max(0, effect.spread);
      return unionBounds(bounds, {
        x: nodeBounds.x + effect.offsetX - padding,
        y: nodeBounds.y + effect.offsetY - padding,
        width: nodeBounds.width + padding * 2,
        height: nodeBounds.height + padding * 2,
      });
    }

    if (effect.type === "layerBlur") {
      const blur = effect.mode === "progressive" ? Math.max(effect.start, effect.end) : effect.blur;
      const padding = Math.max(0, blur) * 3;
      return unionBounds(bounds, {
        x: nodeBounds.x - padding,
        y: nodeBounds.y - padding,
        width: nodeBounds.width + padding * 2,
        height: nodeBounds.height + padding * 2,
      });
    }

    return bounds;
  }, nodeBounds);
}

function identityPrimitive(input: string): string {
  return [
    `<feColorMatrix in="${input}" type="matrix" values="`,
    `1 0 0 0 0 `,
    `0 1 0 0 0 `,
    `0 0 1 0 0 `,
    `0 0 0 1 0" />`,
  ].join("");
}

function unionBounds(
  left: { x: number; y: number; width: number; height: number },
  right: { x: number; y: number; width: number; height: number },
): { x: number; y: number; width: number; height: number } {
  const x1 = Math.min(left.x, right.x);
  const y1 = Math.min(left.y, right.y);
  const x2 = Math.max(left.x + left.width, right.x + right.width);
  const y2 = Math.max(left.y + left.height, right.y + right.height);
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}

function effectColor(effect: Extract<NodeEffect, { type: "dropShadow" | "innerShadow" }>): { hex: string; alpha: number } {
  const [, , , alpha] = oklchToRgba(effect.color);
  return {
    hex: oklchToHex(effect.color),
    alpha,
  };
}

function escapeAttribute(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("\"", "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}
